import { useQuery } from "@tanstack/react-query";
import { boronClient } from "../../api";
import { createError, HTTPErrors } from "../../errors";
import { paths } from "../../lib/api/v1";

const path =
  "/api/v1/sections/multi_section_batch_histories/{multi_section_batch_history_id}";

type Payload = {
  datetimeFrom: string;
  steakMaterialCode?: string;
};

type ResponseBody =
  paths[typeof path]["get"]["responses"]["200"]["content"]["application/json"] & {
    multiSectionBatchHistory: {
      payload: Payload;
    };
  };

export type MultiSectionBatch = Exclude<
  ResponseBody["multiSectionBatchHistory"],
  undefined
>;

type Props = {
  batchId: string;
};

const shouldRefetch = (data: ResponseBody | undefined) => {
  if (data === undefined) {
    return true;
  }

  if (
    data.multiSectionBatchHistory.batchType === "upload_drill" ||
    data.multiSectionBatchHistory.batchType === "update_drill"
  ) {
    return false;
  }

  return ["standby", "processing"].includes(
    data.multiSectionBatchHistory.status,
  );
};

export const useMultiSectionBatchesDetail = ({ batchId }: Props) => {
  const { data, isLoading, error } = useQuery<ResponseBody, HTTPErrors>({
    queryKey: ["multiSectionBatchHistories", batchId],
    queryFn: async () => {
      const { response } = await boronClient.GET(path, {
        params: {
          path: {
            multi_section_batch_history_id: batchId,
          },
        },
      });

      if (response.ok) {
        return response.json();
      }

      throw await createError(response);
    },
    refetchInterval: (query) =>
      shouldRefetch(query.state.data)
        ? 1000 * 3 // 3秒ごとに再取得する
        : false, // 成功・失敗のときは再取得しない
    gcTime: 0, // ログインユーザーを変更した時に再取得されるようにキャッシュしない
  });

  return {
    data,
    isLoading,
    error,
  };
};
